import React, { useState } from 'react';
import { supabase } from '../../utils/supabase';
import { Store, UtensilsCrossed, Palette, CheckCircle, Plus, Trash2, Bot, Zap, LogOut } from 'lucide-react';

const steps = [
  { id: 1, label: 'Restaurant Info', icon: Store },
  { id: 2, label: 'Menu Setup', icon: UtensilsCrossed },
  { id: 3, label: 'Theme', icon: Palette },
  { id: 4, label: 'Done!', icon: CheckCircle },
];

const themes = [
  { name: 'Green', primary: '#16a34a', secondary: '#15803d' },
  { name: 'Blue', primary: '#2563eb', secondary: '#1d4ed8' },
  { name: 'Red', primary: '#dc2626', secondary: '#b91c1c' },
  { name: 'Purple', primary: '#9333ea', secondary: '#7e22ce' },
  { name: 'Orange', primary: '#ea580c', secondary: '#c2410c' },
];

const categories = ['Grills', 'Fast Food', 'Sides', 'Drinks', 'Desserts'];

const categoryEmoji = {
  Grills: '🔥',
  'Fast Food': '🍔',
  Sides: '🥗',
  Drinks: '🥤',
  Desserts: '🍰',
};

export default function Onboarding({ user, onComplete }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [signingOut, setSigningOut] = useState(false);

  const [info, setInfo] = useState({
    name: '',
    nameAr: '',
    phone: '',
    address: '',
    description: '',
  });

  const [menuItems, setMenuItems] = useState([
    { name: '', nameAr: '', category: 'Grills', price: '' },
  ]);

  const [selectedTheme, setSelectedTheme] = useState(themes[0]);

  const handleSignOut = async () => {
    setSigningOut(true);
    await supabase.auth.signOut();
    // App.jsx's onAuthStateChange listener picks up the SIGNED_OUT event
    // and resets user to null, which renders the login screen automatically.
  };

  const validateStep1 = () => {
    if (!info.name.trim()) return 'Restaurant name (English) is required';
    if (!info.phone.trim()) return 'Phone number is required';
    if (!info.address.trim()) return 'Address is required';
    return null;
  };

  const validateStep2 = () => {
    const filled = menuItems.filter(i => i.name || i.price);
    if (filled.length === 0) return null;
    for (let i = 0; i < filled.length; i++) {
      if (filled[i].name && !filled[i].price) return `Please add a price for "${filled[i].name}"`;
      if (filled[i].price && !filled[i].name) return `Please add a name for item ${i + 1}`;
    }
    return null;
  };

  const handleNext = () => {
    setError('');
    if (step === 1) {
      const err = validateStep1();
      if (err) { setError(err); return; }
    }
    if (step === 2) {
      const err = validateStep2();
      if (err) { setError(err); return; }
    }
    setStep(step + 1);
  };

  const addMenuItem = () => {
    setMenuItems([...menuItems, { name: '', nameAr: '', category: 'Grills', price: '' }]);
  };

  const updateMenuItem = (index, field, value) => {
    const updated = [...menuItems];
    updated[index][field] = value;
    setMenuItems(updated);
  };

  const removeMenuItem = (index) => {
    if (menuItems.length === 1) return;
    setMenuItems(menuItems.filter((_, i) => i !== index));
  };

  const handleFinish = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: restaurant, error: restError } = await supabase
        .from('restaurants')
        .upsert({
          email: user.email,
          name: info.name.trim(),
          name_ar: info.nameAr.trim(),
          phone: info.phone.trim(),
          address: info.address.trim(),
          description: info.description.trim(),
          theme_color: selectedTheme.primary,
          onboarded: true,
        }, { onConflict: 'email' })
        .select()
        .single();

      if (restError) throw restError;

      const validItems = menuItems.filter(i => i.name.trim() && i.price);
      if (validItems.length > 0 && restaurant) {
        const { error: menuError } = await supabase.from('menu_items').insert(
          validItems.map(item => ({
            restaurant_id: restaurant.id,
            name: item.name.trim(),
            name_ar: item.nameAr.trim(),
            category: item.category,
            price: Number(item.price),
            available: true,
          }))
        );
        if (menuError) throw menuError;
      }

      setStep(4);
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-3">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg"
              style={{ backgroundColor: selectedTheme.primary }}
            >
              <Bot size={24} className="text-white" />
            </div>
            <span className="text-2xl font-bold text-gray-800">TabBot</span>
          </div>
          <h1 className="text-xl font-bold text-gray-800">Let's set up your restaurant</h1>
          <p className="text-gray-400 text-sm mt-1 flex items-center justify-center gap-1">
            <Zap size={14} className="text-yellow-400" /> Takes only 2 minutes
          </p>

          {/* Escape hatch: signed in as the wrong account, or just want to log out */}
          {user?.email && (
            <button
              onClick={handleSignOut}
              disabled={signingOut}
              className="mt-3 inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition disabled:opacity-50"
            >
              <LogOut size={12} />
              {signingOut ? 'Signing out...' : `Not ${user.email}? Sign out`}
            </button>
          )}
        </div>

        {/* Step indicators */}
        <div className="flex items-center justify-center mb-8">
          {steps.map((s, i) => {
            const Icon = s.icon;
            const isActive = step === s.id;
            const isDone = step > s.id;
            return (
              <React.Fragment key={s.id}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all
                      ${isDone || isActive ? 'text-white shadow-md' : 'bg-gray-200 text-gray-400'}
                      ${isActive ? 'scale-110 shadow-lg' : ''}`}
                    style={isDone || isActive ? { backgroundColor: selectedTheme.primary } : {}}
                  >
                    {isDone ? <CheckCircle size={18} /> : <Icon size={18} />}
                  </div>
                  <span
                    className="text-xs mt-1.5 font-medium"
                    style={isActive ? { color: selectedTheme.primary } : { color: '#9ca3af' }}
                  >
                    {s.label}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div
                    className="h-0.5 w-16 mx-2 mb-5 rounded-full transition-all"
                    style={{ backgroundColor: step > s.id ? selectedTheme.primary : '#e5e7eb' }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">

          {/* Step 1 — Restaurant Info */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-bold text-gray-800">Restaurant Information</h2>
                <p className="text-sm text-gray-400 mt-1">Fields marked with * are required</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Name (English) *</label>
                  <input
                    placeholder="e.g. Half Grill"
                    value={info.name}
                    onChange={e => setInfo({ ...info, name: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition"
                    style={{ '--tw-ring-color': selectedTheme.primary }}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">الاسم بالعربي</label>
                  <input
                    placeholder="نصف شواية"
                    value={info.nameAr}
                    onChange={e => setInfo({ ...info, nameAr: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 text-right transition"
                    dir="rtl"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">WhatsApp Number *</label>
                <input
                  placeholder="+966 5xx xxx xxxx"
                  value={info.phone}
                  onChange={e => setInfo({ ...info, phone: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary transition"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Address *</label>
                <input
                  placeholder="Al Rawdah, Jeddah"
                  value={info.address}
                  onChange={e => setInfo({ ...info, address: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary transition"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                  Description <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  placeholder="Best shawarma in Jeddah..."
                  value={info.description}
                  onChange={e => setInfo({ ...info, description: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none transition"
                  rows={3}
                />
              </div>

              {/* Info banner */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 flex items-center gap-3">
                <Bot size={18} className="text-blue-500 shrink-0" />
                <p className="text-blue-700 text-xs">Your WhatsApp bot will use this info to answer customer questions automatically</p>
              </div>
            </div>
          )}

          {/* Step 2 — Menu */}
          {step === 2 && (
            <div>
              <div className="mb-4">
                <h2 className="text-lg font-bold text-gray-800">Add Your Menu Items</h2>
                <p className="text-sm text-gray-400 mt-1">Optional — you can add more items later from the Menu page</p>
              </div>
              <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                {menuItems.map((item, index) => (
                  <div key={index} className="border border-gray-100 rounded-xl p-4 bg-gray-50">
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <input
                        placeholder="Item name (English)"
                        value={item.name}
                        onChange={e => updateMenuItem(index, 'name', e.target.value)}
                        className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                      />
                      <input
                        placeholder="الاسم بالعربي"
                        value={item.nameAr}
                        onChange={e => updateMenuItem(index, 'nameAr', e.target.value)}
                        className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white text-right"
                        dir="rtl"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3 items-center">
                      <select
                        value={item.category}
                        onChange={e => updateMenuItem(index, 'category', e.target.value)}
                        className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                      >
                        {categories.map(c => (
                          <option key={c}>{categoryEmoji[c]} {c}</option>
                        ))}
                      </select>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">SAR</span>
                        <input
                          placeholder="0"
                          type="number"
                          value={item.price}
                          onChange={e => updateMenuItem(index, 'price', e.target.value)}
                          className="w-full border border-gray-200 rounded-xl pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                        />
                      </div>
                      <button
                        onClick={() => removeMenuItem(index)}
                        disabled={menuItems.length === 1}
                        className="flex items-center justify-center gap-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl py-2 text-sm transition disabled:opacity-30"
                      >
                        <Trash2 size={14} /> Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={addMenuItem}
                className="mt-4 w-full border-2 border-dashed border-gray-200 text-gray-500 hover:border-primary hover:text-primary py-3 rounded-xl text-sm font-medium transition flex items-center justify-center gap-2"
              >
                <Plus size={16} /> Add Another Item
              </button>
            </div>
          )}

          {/* Step 3 — Theme */}
          {step === 3 && (
            <div>
              <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-800">Choose Your Color Theme</h2>
                <p className="text-sm text-gray-400 mt-1">This will apply to your entire TabBot dashboard</p>
              </div>
              <div className="grid grid-cols-5 gap-4">
                {themes.map(theme => (
                  <button
                    key={theme.name}
                    onClick={() => setSelectedTheme(theme)}
                    className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition
                      ${selectedTheme.name === theme.name ? 'border-gray-800 scale-105 shadow-md' : 'border-transparent hover:border-gray-200'}`}
                  >
                    <div
                      className="w-12 h-12 rounded-xl shadow-md flex items-center justify-center"
                      style={{ backgroundColor: theme.primary }}
                    >
                      {selectedTheme.name === theme.name && (
                        <CheckCircle size={20} className="text-white" />
                      )}
                    </div>
                    <span className="text-xs font-semibold text-gray-600">{theme.name}</span>
                  </button>
                ))}
              </div>

              {/* Live Preview */}
              <div className="mt-6 bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-400 mb-3 font-medium uppercase tracking-wide">Preview</p>
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: selectedTheme.primary }}
                    >
                      <span className="text-white font-bold text-sm">
                        {info.name?.[0]?.toUpperCase() || 'T'}
                      </span>
                    </div>
                    <div>
                      <p className="font-bold text-gray-800 text-sm">{info.name || 'My Restaurant'}</p>
                      <p className="text-xs text-gray-400">{info.address || 'Jeddah 🇸🇦'}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      className="px-3 py-1.5 rounded-lg text-white text-xs font-medium"
                      style={{ backgroundColor: selectedTheme.primary }}
                    >
                      ✅ Confirm Order
                    </button>
                    <span
                      className="px-3 py-1.5 rounded-lg text-xs font-medium"
                      style={{ backgroundColor: selectedTheme.primary + '20', color: selectedTheme.primary }}
                    >
                      🟢 Bot Active
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4 — Done */}
          {step === 4 && (
            <div className="text-center py-8">
              <div
                className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
                style={{ backgroundColor: selectedTheme.primary }}
              >
                <CheckCircle size={44} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">You're all set! 🎉</h2>
              <p className="text-gray-500 mb-2">Your TabBot dashboard is ready.</p>
              <p className="text-gray-400 text-sm mb-6">Go to <strong>Bot Settings</strong> to activate your WhatsApp bot.</p>

              <div className="bg-gray-50 rounded-xl p-4 text-left space-y-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">What's next?</p>
                {[
                  '📱 Connect your WhatsApp number in Settings',
                  '🍽️ Add more menu items in Menu Manager',
                  '🤖 Customize your bot in Bot Settings',
                  '📊 Watch orders come in on your Dashboard',
                ].map(tip => (
                  <div key={tip} className="flex items-center gap-2 text-sm text-gray-600">
                    <span>{tip}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mt-4 bg-red-50 border border-red-100 text-red-600 text-sm px-4 py-3 rounded-xl flex items-center gap-2">
              ⚠️ {error}
            </div>
          )}

          {/* Navigation */}
          <div className="flex gap-3 mt-8">
            {step > 1 && step < 4 && (
              <button
                onClick={() => { setStep(step - 1); setError(''); }}
                className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-xl text-sm font-medium hover:bg-gray-50 transition"
              >
                ← Back
              </button>
            )}
            {step < 3 && (
              <button
                onClick={handleNext}
                className="flex-1 text-white py-3 rounded-xl text-sm font-medium transition shadow-sm"
                style={{ backgroundColor: selectedTheme.primary }}
              >
                Continue →
              </button>
            )}
            {step === 3 && (
              <button
                onClick={handleFinish}
                disabled={loading}
                className="flex-1 text-white py-3 rounded-xl text-sm font-medium transition disabled:opacity-60 shadow-sm"
                style={{ backgroundColor: selectedTheme.primary }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Saving...
                  </span>
                ) : 'Finish Setup 🚀'}
              </button>
            )}
            {step === 4 && (
              <button
                onClick={() => onComplete(selectedTheme.primary)}
                className="flex-1 text-white py-3 rounded-xl text-sm font-medium transition shadow-sm"
                style={{ backgroundColor: selectedTheme.primary }}
              >
                Go to Dashboard →
              </button>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          🔒 Your data is encrypted and secure · TabBot 🇸🇦
        </p>
      </div>
    </div>
  );
}